import React, { useEffect, useState } from "react";
import {
  AvgTd,
  SubTitle,
  Table,
  TableContainer,
  Td,
  Th,
  Tr,
} from "./HomeStyle";
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  PieChart,
  Pie,
  LabelList,
  Cell,
} from "recharts";
import {
  getDashBoardCustomerMain1,
  getDashBoardCustomerMain2,
  getDashBoardCustomerMain3,
  getDashBoardCustomerMain4,
} from "../../Service/API";

const UserHome = (props) => {
  const [cuCode, setCuCode] = useState("");

  const [data1, setData1] = useState([]); // 주간 사육동향
  const [data2, setData2] = useState(null); // 산차별 착유우 현황
  const [data3, setData3] = useState(null); // 비유일수별 착유우 현황
  const [data4, setData4] = useState(null); // 산유량별 착유우 현황

  const [chartData2, setChartData2] = useState([]); //산차별 비율 차트 데이터

  const tempDay = new Date();
  tempDay.setDate(tempDay.getDate() - 1);
  const [yesterday, setYesterday] = useState(tempDay);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [maxSupp, setMaxSupp] = useState(0);
  const [maxCow, setMaxCow] = useState(0);
  const [quota, setQuota] = useState(0);

  // 초기 실행
  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        const storedUserId = localStorage.getItem("userId");

        setCuCode(storedUserId);

        if (cuCode) {
          await fetchData(cuCode);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [cuCode]);

  // API 호출 함수 (농가)
  const fetchData = async (cuCode) => {
    try {
      setLoading(true);
      const [result1, result2, result3, result4] = await Promise.all([
        getDashBoardCustomerMain1(cuCode),
        getDashBoardCustomerMain2(cuCode),
        getDashBoardCustomerMain3(cuCode),
        getDashBoardCustomerMain4(cuCode),
      ]);

      setData1(result1 || []);
      setData2(result2 || []);
      setData3(result3 || []);
      setData4(result4 || []);

      const chartdata2 = convertToChartData2(result2);

      setChartData2(chartdata2);

      if (result1 && result1.length > 0) {
        const M_COW_TOT = Math.max(...result1.map((item) => item.COW_TOT || 0));
        const M_SUPP_QTY = Math.max(
          ...result1.map((item) => item.SUPP_QTY || 0)
        );
        const M_QUOTA = Math.max(...result1.map((item) => item.QUOTA || 0));

        setMaxCow(M_COW_TOT);
        setMaxSupp(M_SUPP_QTY);
        setQuota(M_QUOTA);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateYAxisDomain = (values, buffer = 100) => {
    const maxValue = Math.max(...values);
    return Math.ceil(maxValue / 100) * 100 + buffer;
  };

  const calculateAverage = (data, key) => {
    if (!data || data.length === 0) return "";
    const total = data.reduce((sum, item) => sum + (item[key] || 0), 0);
    const average = total / data.length;
    return average === 0 ? "" : average.toFixed(1);
  };

  const calculateAverageRate = (data) => {
    if (!data || data.length === 0) return "-";
    const total = data.reduce((sum, item) => {
      if (item.SUPP_QTY && item.COW_TOT) {
        return sum + item.SUPP_QTY / item.COW_TOT;
      }
      return sum;
    }, 0);
    const average = total / data.length;
    return average === 0 ? "-" : average.toFixed(1);
  };

  const convertToChartData2 = (data) => {
    if (!data) return [];
    return [
      { name: "1산", RAT: data.RAT_1 },
      { name: "2산", RAT: data.RAT_2 },
      { name: "3산", RAT: data.RAT_3 },
      { name: "4산", RAT: data.RAT_4 },
      { name: "5산↑", RAT: data.RAT_5 },
    ];
  };

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#A28DFF"];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, RAT } = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "5px",
          }}
        >
          <p>{`${name} : ${RAT}%`}</p>
        </div>
      );
    }
    return null;
  };

  const chartData2legendPayload = chartData2.map((entry, index) => ({
    id: entry.name,
    type: "square", // 범례 마커 타입 설정
    value: entry.name, // 표시할 DIVNM 값
    color: COLORS[index % COLORS.length],
  }));

  return (
    <>
      <SubTitle width="120px">주간 사육동향</SubTitle>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data1}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="YMD"
            tickFormatter={(value) => {
              if (value.length === 8) {
                const year = value.substring(0, 4); // 연도 추출
                const month = value.substring(4, 6); // 월 추출
                const day = value.substring(6, 8); // 일 추출
                return `${month}-${day}`;
              }
              return value;
            }}
            tick={{
              angle: -45,
              fontSize: 12,
              fill: "black",
            }}
            height={60}
            interval={0}
            tickMargin={22}
          />
          <YAxis
            yAxisId="left"
            domain={[0, Math.ceil(maxCow * 1.2)]} // 두수 최대값에 10% 여유 추가
            tickCount={8} // 눈금을 10개로 설정
            tickFormatter={(value) => value.toLocaleString()}
            label={{
              value: "두수",
              angle: -90,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#333" },
            }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[0, calculateYAxisDomain([maxSupp, quota]) * 1.1]} // Y축 최대값을 10% 늘림
            tickFormatter={(value) => value.toLocaleString()}
            label={{
              value: "유량 (kg)",
              angle: -90,
              offset: 60,
              position: "insideLeft",
              style: { fontSize: 12, fill: "#333" },
            }}
          />
          {/* Tooltip 수정 */}
          <Tooltip
            labelFormatter={(value) => {
              if (value.length === 8) {
                const year = value.substring(0, 4);
                const month = value.substring(4, 6);
                const day = value.substring(6, 8);
                return `${year}-${month}-${day}`;
              }
              return value;
            }}
            formatter={(value, name) => {
              const formattedValue = value.toLocaleString();
              if (name === "COW_TOT") return [formattedValue, "두수"];
              if (name === "SUPP_QTY") return [formattedValue, "유량"];
              return [formattedValue, name];
            }}
          />
          <Legend
            formatter={(value) => {
              if (value === "COW_TOT") return "두수";
              if (value === "SUPP_QTY") return "유량";
              return value;
            }}
          />
          {/* 기준선 추가 - SUPP_QTY 기준 */}
          {quota > 0 && (
            <ReferenceLine
              y={quota} // SUPP_QTY 기준선 값
              yAxisId="right" // 오른쪽 Y축 기준
              stroke="red" // 선 색상
              strokeWidth={3} // 선 두께
              label={{
                value: `쿼터량: ${quota.toLocaleString()}`, // 라벨 텍스트
                position: "top", // 라벨 위치
                fill: "red", // 라벨 색상
                fontSize: 14, // 라벨 폰트 크기
              }}
            />
          )}
          <Bar yAxisId="left" dataKey="COW_TOT" fill="#3498db" />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="SUPP_QTY"
            stroke="#ef0505"
            strokeWidth={2} // 라인 두께를 3으로 설정
          />
        </ComposedChart>
      </ResponsiveContainer>

      <TableContainer>
        <Table>
          <Tr>
            <Th rowSpan={2}>일자</Th>
            <Th colSpan={4}>사육두수</Th>
            <Th rowSpan={2}>납유량(L)</Th>
            <Th rowSpan={2}>두당 산유량(L)</Th>
          </Tr>
          <Tr>
            <Th>초임</Th>
            <Th>건유</Th>
            <Th>착유</Th>
            <Th>계</Th>
          </Tr>
          {/* data1 데이터를 기반으로 행 생성 */}
          {data1 && data1.length > 0 ? (
            data1.map((item, index) => (
              <Tr key={index}>
                <Td textAlign="center">
                  {item.YMD
                    ? `${item.YMD.slice(4, 6)}-${item.YMD.slice(6, 8)}` // "MM-DD" 형식으로 변환
                    : "-"}
                </Td>
                <Td>{item.CNT_NONE?.toLocaleString() ?? ""}</Td>
                <Td>{item.CNT_DRY?.toLocaleString() ?? ""}</Td>
                <Td>{item.CNT_MILK?.toLocaleString() ?? ""}</Td>
                <Td>{item.COW_TOT?.toLocaleString() ?? ""}</Td>
                <Td>{item.SUPP_QTY?.toLocaleString() ?? ""}</Td>
                <Td>
                  {item.COW_TOT && item.SUPP_QTY
                    ? (item.SUPP_QTY / item.COW_TOT || 0).toFixed(1) // 소수점 1자리
                    : ""}
                </Td>
              </Tr>
            ))
          ) : (
            <></>
          )}
          <Tr>
            <AvgTd textAlign="center">평균</AvgTd>
            <AvgTd>{calculateAverage(data1, "CNT_NONE")}</AvgTd>
            <AvgTd>{calculateAverage(data1, "CNT_DRY")}</AvgTd>
            <AvgTd>{calculateAverage(data1, "CNT_MILK")}</AvgTd>
            <AvgTd>{calculateAverage(data1, "COW_TOT")}</AvgTd>
            <AvgTd>{calculateAverage(data1, "SUPP_QTY")}</AvgTd>
            <AvgTd>{calculateAverageRate(data1)}</AvgTd>
          </Tr>
        </Table>
      </TableContainer>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <SubTitle width="120px">착유우 사육현황</SubTitle>
        <div style={{ marginTop: "10px", marginRight: "10px" }}>
          {yesterday.toLocaleDateString()} 기준
        </div>
      </div>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>산유량(kg)</Th>
            <Th>두수</Th>
            <Th>비율(%)</Th>
          </Tr>
          <Tr>
            <Th>1산</Th>
            <Td>{data2 ? (data2.MILK_1 || 0).toFixed(1) : ""}</Td>
            <Td>{data2 ? data2.COW_1 : ""}</Td>
            <Td>{data2 ? data2.RAT_1 : ""}</Td>
          </Tr>
          <Tr>
            <Th>2산</Th>
            <Td>{data2 ? (data2.MILK_2 || 0).toFixed(1) : ""}</Td>
            <Td>{data2 ? data2.COW_2 : ""}</Td>
            <Td>{data2 ? data2.RAT_2 : ""}</Td>
          </Tr>
          <Tr>
            <Th>3산</Th>
            <Td>{data2 ? (data2.MILK_3 || 0).toFixed(1) : ""}</Td>
            <Td>{data2 ? data2.COW_3 : ""}</Td>
            <Td>{data2 ? data2.RAT_3 : ""}</Td>
          </Tr>
          <Tr>
            <Th>4산</Th>
            <Td>{data2 ? (data2.MILK_4 || 0).toFixed(1) : ""}</Td>
            <Td>{data2 ? data2.COW_4 : ""}</Td>
            <Td>{data2 ? data2.RAT_4 : ""}</Td>
          </Tr>
          <Tr>
            <Th>5산</Th>
            <Td>{data2 ? (data2.MILK_5 || 0).toFixed(1) : ""}</Td>
            <Td>{data2 ? data2.COW_5 : ""}</Td>
            <Td>{data2 ? data2.RAT_5 : ""}</Td>
          </Tr>
          <Tr>
            <Th>계</Th>
            <Td>{data2 ? data2.MILK_TOT : ""}</Td>
            <Td>{data2 ? data2.CNT_TOT : ""}</Td>
            <Td>{data2 ? data2.RAT_TOT : ""}</Td>
          </Tr>
        </Table>
      </TableContainer>

      <ResponsiveContainer height={350}>
        <PieChart>
          <Pie
            data={chartData2}
            cx="50%"
            cy="50%"
            outerRadius={120}
            innerRadius={50}
            fill="#8884d8"
            dataKey="RAT"
            labelLine={true}
          >
            <LabelList
              dataKey="RAT"
              position="inside"
              style={{
                fill: "white",
                fontSize: "12px",
              }}
              formatter={(value) => `${value}%`}
            />
            {chartData2.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={COLORS[index % COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend payload={chartData2legendPayload} />
        </PieChart>
      </ResponsiveContainer>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>산유량(kg)</Th>
            <Th>두수</Th>
            <Th>비율(%)</Th>
          </Tr>
          <Tr>
            <Th>초기</Th>
            <Td>{data3 ? (data3.MILK_1 || 0).toFixed(1) : ""}</Td>
            <Td>{data3 ? data3.COW_1 : ""}</Td>
            <Td>{data3 ? data3.RAT_1 : ""}</Td>
          </Tr>
          <Tr>
            <Th>중기</Th>
            <Td>{data3 ? (data3.MILK_2 || 0).toFixed(1) : ""}</Td>
            <Td>{data3 ? data3.COW_2 : ""}</Td>
            <Td>{data3 ? data3.RAT_2 : ""}</Td>
          </Tr>
          <Tr>
            <Th>후기</Th>
            <Td>{data3 ? (data3.MILK_3 || 0).toFixed(1) : ""}</Td>
            <Td>{data3 ? data3.COW_3 : ""}</Td>
            <Td>{data3 ? data3.RAT_3 : ""}</Td>
          </Tr>
          <Tr>
            <Th>계</Th>
            <Td>{data3 ? (data3.MILK_TOT || 0).toFixed(1) : ""}</Td>
            <Td>{data3 ? data3.CNT_TOT : ""}</Td>
            <Td>{data3 ? data3.RAT_TOT : ""}</Td>
          </Tr>
        </Table>
      </TableContainer>

      <TableContainer>
        <Table>
          <Tr>
            <Th>구분</Th>
            <Th>산유량(kg)</Th>
            <Th>두수</Th>
            <Th>비율(%)</Th>
          </Tr>
          <Tr>
            <Th>20kg ↓</Th>
            <Td>{data4 ? (data4.MILK_20 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_20 : ""}</Td>
            <Td>{data4 ? data4.RAT_20 : ""}</Td>
          </Tr>
          <Tr>
            <Th>21~25kg</Th>
            <Td>{data4 ? (data4.MILK_21 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_21 : ""}</Td>
            <Td>{data4 ? data4.RAT_21 : ""}</Td>
          </Tr>
          <Tr>
            <Th>26~30kg</Th>
            <Td>{data4 ? (data4.MILK_26 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_26 : ""}</Td>
            <Td>{data4 ? data4.RAT_26 : ""}</Td>
          </Tr>
          <Tr>
            <Th>31~35kg</Th>
            <Td>{data4 ? (data4.MILK_31 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_31 : ""}</Td>
            <Td>{data4 ? data4.RAT_31 : ""}</Td>
          </Tr>
          <Tr>
            <Th>36~40kg</Th>
            <Td>{data4 ? (data4.MILK_36 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_36 : ""}</Td>
            <Td>{data4 ? data4.RAT_36 : ""}</Td>
          </Tr>
          <Tr>
            <Th>40kg ↑</Th>
            <Td>{data4 ? (data4.MILK_40 || 0).toFixed(1) : ""}</Td>
            <Td>{data4 ? data4.COW_40 : ""}</Td>
            <Td>{data4 ? data4.RAT_40 : ""}</Td>
          </Tr>
        </Table>
      </TableContainer>
    </>
  );
};

export default UserHome;
